import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { StaffShell } from "@/components/StaffShell";
import { Eyebrow } from "@/brand/ui";
import { ArrowLeft, CheckCircle2, Clock, FileText, MessageSquare, ListTodo, GitBranch, Users } from "lucide-react";

// ============================================================================
// PROTÓTIPO FRONTEND — Future CASE Workspace (Fase 4)
// Dados 100% FICTÍCIOS. SEM backend. NÃO converte LEAD em CASE.
// Demonstra a fronteira canónica: LEAD → (CONTRATO + PAGAMENTO) → CASE.
// LEAD (comercial) e CASE (operacional/jurídico) são entidades SEPARADAS.
// ============================================================================

const MOCK_CASE = {
  case_id: "CASE-2026-0042",
  name: "Nacionalidade Portuguesa — Família Ribeiro",
  status: "Em curso",
  responsible: "Dr. Rodrigo Duarte",
  team: ["Dr. Rodrigo Duarte", "Dra. Marta Nunes", "Paralegal João Alves"],
  practice_area: "Mobilidade — Nacionalidade por Descendência",
  clients: ["Ana Ribeiro", "Carlos Ribeiro"],
  source_lead_id: "DAI-C579F0",
  next_action: "Submeter processo na Conservatória dos Registos Centrais",
  next_deadline: "12 Jul 2026",
  drive_ref: "drive://Casos/2026/CASE-0042",
  chat_ref: "spaces/AAA-Ribeiro",
  gemini_ref: "gemini://case/0042/context",
  last_interaction: "Ontem, 16:32 — email do cliente",
};

const MOCK_TASKS = [
  { t: "Recolher certidões de nascimento da linha familiar", who: "Paralegal João", due: "28 Jun", done: true },
  { t: "Validar tradução certificada dos documentos brasileiros", who: "Dra. Marta", due: "02 Jul", done: true },
  { t: "Preparar requerimento e apostilar documentos", who: "Dra. Marta", due: "08 Jul", done: false },
  { t: "Submeter processo na Conservatória", who: "Dr. Rodrigo", due: "12 Jul", done: false },
];
const MOCK_COMMS = [
  { dir: "in", ch: "email", who: "Ana Ribeiro", txt: "Enviei a certidão da minha avó em anexo.", ts: "Ontem 16:32" },
  { dir: "out", ch: "email", who: "Dra. Marta", txt: "Recebido, obrigado. Vamos avançar com a tradução certificada.", ts: "Ontem 17:10" },
  { dir: "in", ch: "chat", who: "Carlos Ribeiro", txt: "Qual o prazo estimado?", ts: "Hoje 09:14" },
];
const MOCK_DOCS = [
  { name: "Certidao_Avo_Maria.pdf", size: "1.2 MB", tag: "Certidão" },
  { name: "Passaporte_Ana.pdf", size: "0.8 MB", tag: "Identificação" },
  { name: "Traducao_Certificada.pdf", size: "2.1 MB", tag: "Tradução" },
];
const MOCK_TIMELINE = [
  { e: "CASO criado a partir do Lead DAI-C579F0 (contrato + pagamento confirmados)", ts: "20 Jun 2026" },
  { e: "Equipa atribuída · pasta Drive criada", ts: "20 Jun 2026" },
  { e: "Documentos iniciais recebidos do cliente", ts: "24 Jun 2026" },
  { e: "Tradução certificada validada", ts: "02 Jul 2026" },
];
const MOCK_DECISIONS = [
  { d: "Aplicar por descendência (avó) e não por residência", by: "Dr. Rodrigo", ts: "20 Jun" },
  { d: "Cliente pai NÃO precisa de aplicar primeiro — linha direta confirmada", by: "Dra. Marta", ts: "26 Jun" },
];

const TABS = [
  { key: "overview", label: "Visão geral", icon: FileText },
  { key: "tasks", label: "Tarefas", icon: ListTodo },
  { key: "comms", label: "Comunicações", icon: MessageSquare },
  { key: "docs", label: "Documentos", icon: FileText },
  { key: "timeline", label: "Cronologia", icon: Clock },
  { key: "decisions", label: "Decisões", icon: GitBranch },
  { key: "team", label: "Equipa", icon: Users },
];

const card = { background: "#fff", border: "1px solid var(--line)", borderRadius: 4, padding: 24 };
const label = { fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--silver-mid)" };

function Field({ k, v }) {
  return (
    <div>
      <div style={label}>{k}</div>
      <div style={{ fontSize: 14, color: "var(--navy)", marginTop: 4 }}>{v}</div>
    </div>
  );
}

export default function CaseWorkspace() {
  const [tab, setTab] = useState("overview");
  const navigate = useNavigate();
  const c = MOCK_CASE;

  return (
    <StaffShell>
      <div data-testid="case-prototype" style={{ maxWidth: 1200 }}>
        {/* Prototype banner */}
        <div data-testid="case-proto-banner" style={{ background: "var(--navy)", color: "var(--off-white)", borderRadius: 4, padding: "14px 20px", marginBottom: 20, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--gold)" }}>Protótipo</span>
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.85)" }}>Workspace de CASO (Fase 4) — dados fictícios, sem backend. LEAD e CASO são entidades separadas.</span>
        </div>

        {/* Lifecycle boundary */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
          <button onClick={() => navigate("/painel")} data-testid="case-back" style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent", border: "1px solid var(--line)", borderRadius: 2, padding: "8px 14px", fontSize: 12, color: "var(--silver-dark)", cursor: "pointer" }}>
            <ArrowLeft size={14} /> Pipeline (Leads)
          </button>
          <span style={{ ...label, padding: "6px 10px", border: "1px solid var(--line)", borderRadius: 999 }}>LEAD</span>
          <span style={{ color: "var(--gold-deep)" }}>→</span>
          <span style={{ ...label, padding: "6px 10px", background: "var(--gold-soft, #f3ecdd)", borderRadius: 999, color: "var(--gold-deep)" }}>Contrato + Pagamento (gate)</span>
          <span style={{ color: "var(--gold-deep)" }}>→</span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", padding: "6px 10px", background: "var(--navy)", color: "var(--off-white)", borderRadius: 999 }}>CASE</span>
        </div>

        {/* Header */}
        <div style={{ ...card, marginBottom: 20 }}>
          <Eyebrow gold>{c.case_id} · origem lead {c.source_lead_id}</Eyebrow>
          <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 30, color: "var(--navy)", margin: "12px 0 20px" }}>{c.name}</h1>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 18 }}>
            <Field k="Estado" v={<span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--gold-deep)" }}><CheckCircle2 size={14} /> {c.status}</span>} />
            <Field k="Advogado responsável" v={c.responsible} />
            <Field k="Área de prática" v={c.practice_area} />
            <Field k="Cliente(s)" v={c.clients.join(", ")} />
            <Field k="Próxima ação" v={c.next_action} />
            <Field k="Próximo prazo" v={c.next_deadline} />
            <Field k="Última interação" v={c.last_interaction} />
            <Field k="Drive" v={c.drive_ref} />
            <Field k="Google Chat" v={c.chat_ref} />
            <Field k="Gemini" v={c.gemini_ref} />
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`case-tab-${t.key}`}
                style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, letterSpacing: "0.06em", padding: "10px 15px", borderRadius: 2, cursor: "pointer",
                  border: `1px solid ${active ? "var(--navy)" : "var(--line)"}`, background: active ? "var(--navy)" : "transparent", color: active ? "var(--off-white)" : "var(--silver-dark)" }}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        <div style={card}>
          {tab === "overview" && <p style={{ fontSize: 14, lineHeight: 1.7, color: "var(--silver-dark)", margin: 0 }}>Processo de nacionalidade por descendência da família Ribeiro. Documentação recolhida e tradução certificada validada. Fase atual: preparação do requerimento e apostila, seguida de submissão na Conservatória dos Registos Centrais. Contexto completo disponível no Drive e no espaço Google Chat associados a este caso.</p>}
          {tab === "tasks" && <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{MOCK_TASKS.map((t, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <CheckCircle2 size={16} style={{ color: t.done ? "var(--gold-deep)" : "var(--line)" }} />
              <span style={{ flex: 1, fontSize: 14, color: "var(--navy)", textDecoration: t.done ? "line-through" : "none", opacity: t.done ? 0.6 : 1 }}>{t.t}</span>
              <span style={{ fontSize: 12, color: "var(--silver-mid)" }}>{t.who} · {t.due}</span>
            </div>))}</div>}
          {tab === "comms" && <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{MOCK_COMMS.map((m, i) => (
            <div key={i} style={{ borderLeft: `2px solid ${m.dir === "in" ? "var(--gold-deep)" : "var(--navy)"}`, paddingLeft: 14 }}>
              <div style={{ ...label }}>{m.ch} · {m.dir === "in" ? "recebida" : "enviada"} · {m.who} · {m.ts}</div>
              <div style={{ fontSize: 14, color: "var(--navy)", marginTop: 4 }}>{m.txt}</div>
            </div>))}</div>}
          {tab === "docs" && <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{MOCK_DOCS.map((d, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--line)" }}>
              <FileText size={16} style={{ color: "var(--gold-deep)" }} />
              <span style={{ flex: 1, fontSize: 14, color: "var(--navy)" }}>{d.name}</span>
              <span style={{ ...label }}>{d.tag}</span>
              <span style={{ fontSize: 12, color: "var(--silver-mid)" }}>{d.size}</span>
            </div>))}</div>}
          {tab === "timeline" && <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{MOCK_TIMELINE.map((e, i) => (
            <div key={i} style={{ display: "flex", gap: 12 }}>
              <span style={{ width: 7, height: 7, borderRadius: 999, background: "var(--gold-deep)", marginTop: 6, flexShrink: 0 }} />
              <div><div style={{ fontSize: 14, color: "var(--navy)" }}>{e.e}</div><div style={{ ...label, marginTop: 2 }}>{e.ts}</div></div>
            </div>))}</div>}
          {tab === "decisions" && <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{MOCK_DECISIONS.map((d, i) => (
            <div key={i} style={{ borderLeft: "2px solid var(--navy)", paddingLeft: 14 }}>
              <div style={{ fontSize: 14, color: "var(--navy)" }}>{d.d}</div>
              <div style={{ ...label, marginTop: 2 }}>{d.by} · {d.ts}</div>
            </div>))}</div>}
          {tab === "team" && <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>{MOCK_CASE.team.map((m, i) => (
            <span key={i} style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid var(--line)", borderRadius: 999, padding: "8px 14px", fontSize: 13, color: "var(--navy)" }}><Users size={14} style={{ color: "var(--gold-deep)" }} /> {m}</span>))}</div>}
        </div>
      </div>
    </StaffShell>
  );
}
